// Typed fetch helpers for the API. Throws on non-2xx.

export type Category = {
  id: number;
  name: string;
  amount_cents: number;
  kind: "expense" | "savings" | "income";
  sort_order: number;
  archived: boolean;
  created_at: number;
};

export type Settings = {
  savings_starting_balance_cents?: string;
  savings_starting_as_of_iso?: string;
};

export type Account = {
  id: number;
  name: string;
  kind: "checking" | "credit_card";
  parser: "main_checking" | "chase_reserve" | "chase_amazon";
  created_at: number;
};

export type Upload = {
  id: number;
  account_id: number;
  account_name: string;
  filename: string;
  earliest_date_iso: string;
  latest_date_iso: string;
  row_count: number;
  rows_inserted: number;
  rows_duplicated: number;
  uploaded_at: number;
};

export type UploadRowInput = {
  posted_at_iso: string;
  description: string;
  amount_cents: number;
  raw_classification?: string;
};

export type TransactionSplit = {
  id: number;
  transaction_id: number;
  category_id: number;
  category_name: string;
  amount_cents: number;
};

export type Transaction = {
  id: number;
  account_id: number;
  account_name: string;
  posted_at_iso: string;
  description: string;
  amount_cents: number;
  raw_classification: string | null;
  is_transfer: boolean;
  category_id: number | null;
  category_name: string | null;
  misc_income_id: number | null;
  misc_income_label: string | null;
  notes: string | null;
  dedup_ordinal: number;
  created_at: number;
  splits: TransactionSplit[];
  suggested_category_id: number | null;
  suggested_category_name: string | null;
};

export type MiscIncomeBucket = {
  id: number;
  label: string;
  amount_cents: number;
  occurred_at_iso: string;
  source_tx_id: number | null;
  notes: string | null;
  created_at: number;
  attached_count: number;
  attached_total_cents: number;
  remaining_cents: number;
};

export type TransactionsResponse = {
  month: string;
  counts: { total: number; uncategorized: number; transfers: number };
  transactions: Transaction[];
};

export type CategoryStatus = {
  id: number;
  name: string;
  kind: "expense" | "savings" | "income";
  budget_cents: number;
  spent_cents: number;
  remaining_cents: number;
  over_cents: number;
  sort_order: number;
};

export type MonthDelta = {
  month: string;
  savings_contribution_cents: number;
  overspending_cents: number;
  delta_cents: number;
};

export type MiscIncomeSummary = {
  id: number;
  label: string;
  amount_cents: number;
  occurred_at_iso: string;
  attached_count: number;
  attached_total_cents: number;
  remaining_cents: number;
};

export type ScoreboardData = {
  month: string;
  budget_total_cents: number;
  spent_total_cents: number;
  remaining_total_cents: number;
  total_income_cents: number;
  total_expenses_cents: number;
  by_category: CategoryStatus[];
  uncategorized_count: number;
  misc_income: MiscIncomeSummary[];
  savings: {
    starting_balance_cents: number;
    starting_as_of_iso: string;
    current_balance_cents: number;
    this_month: MonthDelta;
    prior_month_deltas: MonthDelta[];
  };
};

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`API ${status}: ${JSON.stringify(payload)}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
  const text = await r.text();
  const payload = text ? JSON.parse(text) : null;
  if (!r.ok) throw new ApiError(r.status, payload);
  return payload as T;
}

export const api = {
  listCategories: (includeArchived = false) =>
    request<{ categories: Category[] }>(
      `/api/categories${includeArchived ? "?includeArchived=1" : ""}`,
    ),
  createCategory: (input: { name: string; amount: number; kind?: Category["kind"] }) =>
    request<{ category: Category }>("/api/categories", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateCategory: (id: number, input: Partial<Pick<Category, "name" | "kind" | "sort_order" | "archived">> & { amount?: number }) =>
    request<{ category: Category }>(`/api/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteCategory: (id: number) =>
    request<{ deleted?: boolean; archived?: boolean }>(`/api/categories/${id}`, {
      method: "DELETE",
    }),

  getSettings: () => request<{ settings: Settings }>("/api/settings"),
  updateSettings: (input: {
    savings_starting_balance_cents?: string | number;
    savings_starting_as_of_iso?: string;
  }) =>
    request<{ settings: Settings }>("/api/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  listAccounts: () => request<{ accounts: Account[] }>("/api/accounts"),

  listUploads: () => request<{ uploads: Upload[] }>("/api/uploads"),
  createUpload: (input: {
    account_id: number;
    filename: string;
    rows: UploadRowInput[];
  }) =>
    request<{ upload: Upload }>("/api/uploads", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  listTransactions: (opts: {
    month?: string;
    account_id?: number;
    category_id?: number;
    status?: "all" | "uncategorized" | "categorized" | "transfer";
  } = {}) => {
    const q = new URLSearchParams();
    if (opts.month) q.set("month", opts.month);
    if (opts.account_id) q.set("account_id", String(opts.account_id));
    if (opts.category_id) q.set("category_id", String(opts.category_id));
    if (opts.status) q.set("status", opts.status);
    const qs = q.toString();
    return request<TransactionsResponse>(`/api/transactions${qs ? `?${qs}` : ""}`);
  },
  updateTransaction: (
    id: number,
    patch: {
      category_id?: number | null;
      is_transfer?: boolean;
      notes?: string | null;
      misc_income_id?: number | null;
    },
  ) =>
    request<{ ok: true }>(`/api/transactions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  rescanTransactions: () =>
    request<{
      ok: true;
      scanned: number;
      categorized: number;
      transferred: number;
    }>(`/api/transactions/rescan`, { method: "POST" }),

  getScoreboard: (month?: string) => {
    const qs = month ? `?month=${month}` : "";
    return request<ScoreboardData>(`/api/scoreboard${qs}`);
  },
  setSplits: (
    id: number,
    splits: { category_id: number; amount_cents: number }[],
  ) =>
    request<{ ok: true; splits: number }>(`/api/transactions/${id}/splits`, {
      method: "PUT",
      body: JSON.stringify({ splits }),
    }),
  clearSplits: (id: number) =>
    request<{ ok: true }>(`/api/transactions/${id}/splits`, {
      method: "DELETE",
    }),

  listMiscIncome: () =>
    request<{ misc_income: MiscIncomeBucket[] }>(`/api/misc-income`),
  createMiscIncome: (input: { transaction_id: number; label: string; notes?: string }) =>
    request<{ misc_income: MiscIncomeBucket }>(`/api/misc-income`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateMiscIncome: (
    id: number,
    patch: { label?: string; amount?: number; notes?: string | null },
  ) =>
    request<{ ok: true }>(`/api/misc-income/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteMiscIncome: (id: number) =>
    request<{ ok: true }>(`/api/misc-income/${id}`, { method: "DELETE" }),
  closeMiscIncome: (id: number, disposition: "savings" | "discard") =>
    request<{
      ok: true;
      closed: true;
      disposition: "savings" | "discard";
      remaining_cents: number;
      savings_transfer_cents: number;
    }>(`/api/misc-income/${id}/close`, {
      method: "POST",
      body: JSON.stringify({ disposition }),
    }),

  simplefinConfigure: (input: { setup_token?: string; access_url?: string }) =>
    request<{ ok: true; access_url_saved: true }>(`/api/simplefin/configure`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  simplefinStatus: () =>
    request<{
      configured: boolean;
      fetch_error: string | null;
      remote_accounts: { id: string; name: string; org: string; balance: string }[];
      local_accounts: {
        id: number;
        name: string;
        simplefin_account_id: string | null;
        simplefin_last_sync_at: number | null;
      }[];
    }>(`/api/simplefin/status`),
  simplefinMap: (account_id: number, simplefin_account_id: string | null) =>
    request<{ ok: true }>(`/api/simplefin/map`, {
      method: "POST",
      body: JSON.stringify({ account_id, simplefin_account_id }),
    }),
  simplefinSync: () =>
    request<{
      ok: true;
      synced_accounts: number;
      new_transactions: number;
      per_account: { account_id: number; name: string; new: number }[];
      errors: string[];
      warning?: string;
    }>(`/api/simplefin/sync`, { method: "POST" }),
};
